
import { computed, defineComponent, onMounted, ref } from 'vue'
import { useStore } from '@/composables/use-store'
import rest from '@/rest'
import { Team, TeamCreate, TeamUpdate } from '@/types/teams'
import { RestError } from '@/rest/errors'

interface EditableTeam {
  id: string
  campaign_id: string
  key: string
  name: Record<string, string>
  latitude: number | null
  longitude: number | null
}

function toEditable (team: Team): EditableTeam {
  return {
    id: team.id,
    campaign_id: team.campaign_id,
    key: team.key,
    name: { ...(team.name || {}) },
    latitude: team.latitude,
    longitude: team.longitude
  }
}

export default defineComponent({
  name: 'CampaignTeams',
  setup () {
    const store = useStore()

    const loading = ref(false)
    const saving = ref(false)
    const creating = ref(false)
    const teams = ref<Team[]>([])
    const editableTeams = ref<EditableTeam[]>([])

    const newTeamKey = ref('')
    const deleteDialog = ref(false)
    const teamToDelete = ref<Team | null>(null)
    const deleting = ref(false)
    const deleteError = ref<string | null>(null)

    const campaignId = computed<string>(() => store.getters['campaigns/currentId'])
    const localeCodes = computed<string[]>(() => store.getters['campaigns/currentLocaleCodes'] || [])

    const isNewKeyTaken = computed(() => {
      const key = newTeamKey.value.trim()
      if (!key) return false
      return teams.value.some(t => t.key === key)
    })
    const isNewTeamValid = computed(() => {
      return newTeamKey.value.trim().length > 0 && !isNewKeyTaken.value
    })

    const showSnack = (content: string, color = 'success') => {
      store.dispatch('snackbar/SHOW_MESSAGE', { content, color }, { root: true })
    }

    const loadTeams = async () => {
      if (!campaignId.value) return
      loading.value = true
      try {
        teams.value = await rest.teams.listTeams(campaignId.value)
        editableTeams.value = teams.value.map(toEditable)
      } finally {
        loading.value = false
      }
    }

    const onNewKeyInput = () => {
      newTeamKey.value = newTeamKey.value.toLowerCase().trim()
    }

    const createTeam = async () => {
      if (!isNewTeamValid.value) return
      creating.value = true
      try {
        const initialName: Record<string, string> = {}
        for (const code of localeCodes.value) {
          initialName[code] = ''
        }
        const payload: TeamCreate = {
          key: newTeamKey.value.trim(),
          name: initialName,
          latitude: null,
          longitude: null
        }
        await rest.teams.createTeam(campaignId.value, payload)
        newTeamKey.value = ''
        showSnack('Team added')
        await loadTeams()
      } catch (err) {
        if (err instanceof RestError && err.status === 409) {
          showSnack('A team with that key already exists', 'error')
        } else {
          showSnack('Failed to create team', 'error')
          throw err
        }
      } finally {
        creating.value = false
      }
    }

    const isTeamDirty = (index: number): boolean => {
      const original = teams.value[index]
      const current = editableTeams.value[index]
      if (!original || !current) return false
      if (original.key !== current.key) return true
      if (original.latitude !== current.latitude) return true
      if (original.longitude !== current.longitude) return true
      const origNameKeys = Object.keys(original.name || {})
      const currNameKeys = Object.keys(current.name || {})
      const allKeys = new Set([...origNameKeys, ...currNameKeys])
      for (const k of allKeys) {
        if ((original.name?.[k] || '') !== (current.name?.[k] || '')) {
          return true
        }
      }
      return false
    }

    const resetTeam = (index: number) => {
      const original = teams.value[index]
      if (!original) return
      editableTeams.value.splice(index, 1, toEditable(original))
    }

    const saveTeam = async (index: number) => {
      const original = teams.value[index]
      const current = editableTeams.value[index]
      if (!original || !current) return
      const patch: TeamUpdate = {}
      if (current.key !== original.key) patch.key = current.key
      if (current.latitude !== original.latitude) patch.latitude = current.latitude
      if (current.longitude !== original.longitude) patch.longitude = current.longitude
      // Send the full name dict if anything changed within it
      const origNameKeys = Object.keys(original.name || {})
      const currNameKeys = Object.keys(current.name || {})
      const allKeys = new Set([...origNameKeys, ...currNameKeys])
      let nameChanged = false
      for (const k of allKeys) {
        if ((original.name?.[k] || '') !== (current.name?.[k] || '')) {
          nameChanged = true
          break
        }
      }
      if (nameChanged) {
        patch.name = { ...current.name }
      }
      if (Object.keys(patch).length === 0) return

      saving.value = true
      try {
        const updated = await rest.teams.updateTeam(campaignId.value, current.id, patch)
        teams.value.splice(index, 1, updated)
        editableTeams.value.splice(index, 1, toEditable(updated))
        showSnack('Team saved')
      } catch (err) {
        if (err instanceof RestError && err.status === 409) {
          showSnack('A team with that key already exists', 'error')
        } else {
          showSnack('Failed to save team', 'error')
          throw err
        }
      } finally {
        saving.value = false
      }
    }

    const confirmDelete = (team: EditableTeam) => {
      teamToDelete.value = teams.value.find(t => t.id === team.id) || null
      deleteError.value = null
      deleteDialog.value = true
    }

    const closeDeleteDialog = () => {
      deleteDialog.value = false
      teamToDelete.value = null
      deleteError.value = null
    }

    const performDelete = async () => {
      const target = teamToDelete.value
      if (!target) return
      deleting.value = true
      try {
        await rest.teams.deleteTeam(campaignId.value, target.id)
        showSnack('Team deleted')
        closeDeleteDialog()
        await loadTeams()
      } catch (err) {
        if (err instanceof RestError && err.status === 409) {
          const count = err.json?.account_count
          if (typeof count === 'number') {
            deleteError.value = (
              `This team is currently assigned to ${count} campaign account(s). ` +
              'Reassign or remove those accounts before deleting the team.'
            )
          } else {
            deleteError.value = 'This team cannot be deleted because it is in use.'
          }
        } else {
          deleteError.value = 'Failed to delete team. Please try again.'
        }
      } finally {
        deleting.value = false
      }
    }

    onMounted(() => {
      loadTeams()
    })

    return {
      loading,
      saving,
      creating,
      teams,
      editableTeams,
      newTeamKey,
      isNewKeyTaken,
      isNewTeamValid,
      localeCodes,
      onNewKeyInput,
      createTeam,
      isTeamDirty,
      resetTeam,
      saveTeam,
      deleteDialog,
      teamToDelete,
      deleting,
      deleteError,
      confirmDelete,
      closeDeleteDialog,
      performDelete
    }
  }
})
